import { useUnit } from 'effector-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $conflict,
  $importOffer,
  conflictResolved,
  importConfirmed,
  importDismissed,
  type ConflictChoice,
} from '../../entities/library';
import { formatRelativeTime } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Button, Dialog, DialogContent } from '../../shared/ui';

/** App-wide sync dialogs: resolving a conflict, and importing local projects after sign-in. */
export function SyncDialogs() {
  return (
    <>
      <ConflictDialog />
      <ImportDialog />
    </>
  );
}

function ConflictDialog() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [conflict, resolve] = useUnit([$conflict, conflictResolved]);
  const [now] = useState(() => Date.now());
  if (!conflict) return null;
  const name = conflict.project.name;
  const choose = (choice: ConflictChoice) => () => {
    resolve({ choice, copyName: t('sync.conflictCopy', { name }) });
  };
  const options: { choice: ConflictChoice; label: string; hint: string; primary?: boolean }[] = [
    { choice: 'mine', label: t('sync.keepMine'), hint: t('sync.keepMineHint'), primary: true },
    { choice: 'theirs', label: t('sync.takeTheirs'), hint: t('sync.takeTheirsHint') },
    { choice: 'both', label: t('sync.keepBoth'), hint: t('sync.keepBothHint') },
  ];
  return (
    <Dialog open>
      <DialogContent
        title={t('sync.conflictTitle')}
        description={t('sync.conflictBody', {
          name,
          time: formatRelativeTime(conflict.remote.updatedAt, now, locale),
        })}
        closeLabel={t('common.close')}
        hideClose
      >
        <ul className="space-y-2">
          {options.map((o) => (
            <li key={o.choice}>
              <Button
                variant={o.primary ? 'primary' : 'secondary'}
                className="h-auto w-full flex-col items-start gap-0.5 py-2 text-left"
                onClick={choose(o.choice)}
              >
                <span className="font-medium">{o.label}</span>
                <span className="text-xs font-normal">{o.hint}</span>
              </Button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog() {
  const { t } = useTranslation();
  const [offer, confirm, dismiss] = useUnit([$importOffer, importConfirmed, importDismissed]);
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  if (offer.length === 0) return null;
  const chosen = offer.filter((m) => !unchecked.has(m.id)).map((m) => m.id);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent
        title={t('sync.importTitle')}
        description={t('sync.importBody')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="ghost" onClick={dismiss}>
              {t('sync.notNow')}
            </Button>
            <Button
              variant="primary"
              disabled={chosen.length === 0}
              onClick={() => {
                confirm(chosen);
              }}
            >
              {t('sync.import', { count: chosen.length })}
            </Button>
          </>
        }
      >
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {offer.map((meta) => (
            <li key={meta.id}>
              <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-bg-3">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--accent)]"
                  checked={!unchecked.has(meta.id)}
                  onChange={(e) => {
                    const next = new Set(unchecked);
                    if (e.target.checked) next.delete(meta.id);
                    else next.add(meta.id);
                    setUnchecked(next);
                  }}
                />
                {meta.name}
              </label>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
